import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Modal, Button } from './ui';

interface QuotaDetails {
  quota_cpu?: number;
  cpu_quota?: number;
  used_cpu?: number;
  cpu_used?: number;
  quota_ram?: number;
  ram_quota?: number;
  used_ram?: number;
  ram_used?: number;
  quota_stockage?: number;
  storage_quota?: number;
  used_stockage?: number;
  storage_used?: number;
}

interface QuotaRecommendation {
  type?: string;
  resource?: string;
  current_quota?: number | string;
  needed_increase?: number | string;
  suggested_quota?: number | string;
}

interface QuotaErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotaError: {
    message: string;
    quota_exceeded: string[];
    recommendations: (string | QuotaRecommendation)[];
    quota_details: QuotaDetails;
  };
}

const QuotaErrorModal: React.FC<QuotaErrorModalProps> = ({
  isOpen,
  onClose,
  quotaError
}) => {
  // Namespace `common` : TOUTES les cles de ce modal y vivent, aucune n'est dans
  // `projects`. Charger `projects` obligeait chaque appel a repeter
  // `{ ns: 'common' }` -- et deux l'avaient oublie, si bien que « quotaError.title »
  // s'affichait BRUT a l'utilisateur au moment precis ou il fallait lui expliquer
  // un refus. i18next ne signale pas une cle absente, il rend son nom.
  const { t } = (useTranslation as any)('common');
  const { message, quota_exceeded, recommendations, quota_details } = quotaError;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('quotaError.title') as string} size="lg">
      <div className="p-6">
        {/* Header with icon */}
        <div className="flex items-center mb-4">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-500/12">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-400" aria-hidden="true" />
          </div>
          <div className="ml-4">
            <p className="text-sm text-text-tertiary">
              {t('quotaError.insufficientResources')}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Main message */}
          <div className="bg-red-500/12 border-l-4 border-red-400 p-4">
            <p className="text-red-400 font-medium">{message}</p>
          </div>

          {/* Exceeded resources */}
          {quota_exceeded && quota_exceeded.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-2">
                {t('quotaError.exceededResources')}
              </h4>
              <ul className="space-y-1">
                {quota_exceeded.map((resource: string, index: number) => (
                  <li key={index} className="flex items-center text-sm">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    <span className="text-red-400 font-medium">{resource}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Current quota details */}
          {quota_details && Object.keys(quota_details).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-2">
                {t('quotaError.currentQuota')}
              </h4>
              <div className="bg-surface-3 rounded-md p-3">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  {(quota_details.quota_cpu || quota_details.cpu_quota) && (
                    <div>
                      <div className="text-text-tertiary">CPU</div>
                      <div className="font-medium text-text-primary">
                        {quota_details.used_cpu || quota_details.cpu_used || 0}/{quota_details.quota_cpu || quota_details.cpu_quota} cores
                      </div>
                    </div>
                  )}
                  {(quota_details.quota_ram || quota_details.ram_quota) && (
                    <div>
                      <div className="text-text-tertiary">RAM</div>
                      <div className="font-medium text-text-primary">
                        {quota_details.used_ram || quota_details.ram_used || 0}/{quota_details.quota_ram || quota_details.ram_quota} GB
                      </div>
                    </div>
                  )}
                  {(quota_details.quota_stockage || quota_details.storage_quota) && (
                    <div>
                      <div className="text-text-tertiary">{t('quotaError.storage')}</div>
                      <div className="font-medium text-text-primary">
                        {quota_details.used_stockage || quota_details.storage_used || 0}/{quota_details.quota_stockage || quota_details.storage_quota} GB
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {recommendations && recommendations.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-2">
                {t('quotaError.recommendations')}
              </h4>
              <ul className="space-y-2">
                {recommendations.map((recommendation: string | QuotaRecommendation, index: number) => {
                  let recMessage = '';

                  if (typeof recommendation === 'string') {
                    recMessage = recommendation;
                  } else if (typeof recommendation === 'object') {
                    const { type, resource, current_quota, needed_increase, suggested_quota } = recommendation;

                    if (type === 'increase_project_quota') {
                      recMessage = `Augmenter le quota ${resource} du projet de ${current_quota || 'N/A'} à ${suggested_quota || 'N/A'}`;
                    } else if (type === 'reduce_application_request') {
                      recMessage = `Réduire les ressources ${resource} demandées par l'application (actuellement ${needed_increase || 'N/A'})`;
                    } else {
                      // Fallback pour d'autres types
                      recMessage = `${type}: ${resource} - de ${current_quota || 'N/A'} à ${suggested_quota || 'N/A'}`;
                    }
                  } else {
                    recMessage = String(recommendation);
                  }

                  return (
                    <li key={index} className="flex items-start text-sm">
                      <span className="w-2 h-2 bg-accent-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></span>
                      <span className="text-text-secondary">{recMessage}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Action notice */}
          <div className="bg-accent-500/12 border-l-4 border-accent-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-blue-500 text-xl" aria-hidden="true">i</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-accent-400">
                  {t('quotaError.howToResolve')}
                </h3>
                <div className="mt-2 text-sm text-accent-400">
                  <ul className="list-disc list-inside space-y-1">
                    <li>{t('quotaError.contactAdmin')}</li>
                    <li>{t('quotaError.reduceResources')}</li>
                    <li>{t('quotaError.deleteExistingApps')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - sticky at bottom */}
      <div className="flex justify-end p-4 border-t border-border-0 sticky bottom-0 bg-surface-2">
        <Button variant="secondary" onClick={onClose}>
          Fermer
        </Button>
      </div>
    </Modal>
  );
};

export default QuotaErrorModal;
